// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AddressForm, { AddressFormRef } from "../../../templates/payment/AddressForm";
import axios from "axios";
import { InfoBtn } from "../../Payment/InfoBtn";
import { useTranslation } from "react-i18next";
import { NoticeType } from "../../../../types/settingsTypes";
import { getImageUrl } from "../../../../commons/assets_path";
import { PageLoader } from "../../Loaders/PageLoader";

axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

type Props = {
  client: string;
  setNotice: React.Dispatch<React.SetStateAction<NoticeType>>;
  countryCode: string;
  locale: string;
};

type CompanyInfoType = {
  company_name: string;
  workspace_name: string;
  address: {
    postal_code: number;
    prefecture: string;
    city: string;
    street: string;
    building: string;
  };
  billing_email: string;
};

const BillingInfoForm = ({ client, setNotice, countryCode, locale }: Props) => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState(null);
  const [billingInfo, setBillingInfo] = useState<CompanyInfoType | null>(null);
  const [billingEmail, setBillingEmail] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [errMessage, setErrorMessage] = useState<{
    billingEmail: string;
    workspaceName: string;
  } | null>(null);
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setLoading] = useState(true);

  const stripePromise = loadStripe(process.env.VITE_STRIPE_API_KEY);

  const addressFormRef = useRef<AddressFormRef>(null);

  const submit = async () => {
    if (addressFormRef.current) {
      const { value, complete, errors } = await addressFormRef.current.getAddressFormData();

      if (complete) {
        setIsSubmit(true);
        const { address, name } = value;
        const endpoint = `/${client}/settings/billing`;
        const params = {
          company_name: name,
          workspace_name: workspaceName,
          address: {
            postal_code: address.postal_code,
            prefecture: address.state,
            city: address.city,
            street: address.line1,
            building: address.line2,
          },
          billing_email: billingEmail,
        };
        const result = await axios.patch(endpoint, params).catch((error) => {
          return null;
        });

        if (result) {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
          setNotice({ type: "success", message: "請求先情報の更新が完了しました｡" });
        }
        setIsSubmit(false);
      }
    }
  };

  const changeBillings = () => {
    setErrorMessage(null);
    const reg = new RegExp(/^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/);
    const err = {
      workspaceName: "",
      billingEmail: "",
    };

    if (!workspaceName || !billingEmail) {
      err.workspaceName = !workspaceName ? t("EditBillings.ワークスペース名は必須です") : "";
      err.billingEmail = !billingEmail ? t("EditBillings.請求先メールアドレスは必須です") : "";

      if (billingEmail && !billingEmail.match(reg)) {
        err.billingEmail = t("EditBillings.正しいメールアドレスの形式を入力してください");
      }
      setErrorMessage(err);
      return;
    }

    submit();
  };

  const getBillings = async () => {
    const result = await axios.get(`/${client}/settings/billing/retrieve`);

    if (result) {
      setBillingInfo(result.data);
      setBillingEmail(result.data.billing_email);
      setWorkspaceName(result.data.workspace_name);
    }
    setLoading(false);
  };

  const getClientSecret = async () => {
    await axios.get(`/${client}/api_cards/client_secret`).then((res) => {
      setClientSecret(res.data.client_secret);
    });
  };

  const init = async () => {
    await Promise.all([getClientSecret(), getBillings()]);
  };

  useEffect(() => {
    init();
  }, []);

  return clientSecret && billingInfo && !loading ? (
    <div style={{ maxWidth: 664 }}>
      <div className="field" style={{ marginBottom: 31 }}>
        <label className="required d-flex align-items-center">
          {t("EditBillings.ワークスペース名")}
          <span className="ml-1 d-flex align-items-center" style={{ fontSize: 12, color: "#FF4958" }}>
            <img src={getImageUrl("images/icons/asterisk.svg")} alt="" />
            {t("EditBillings.必須")}
          </span>
        </label>
        <input
          className={`form-control${errMessage?.workspaceName ? ` is-invalid` : ""}`}
          type="text"
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
          style={{ backgroundImage: "none", width: 400 }}
        />
        {errMessage?.workspaceName && <div className="invalid-feedback">{errMessage.workspaceName}</div>}
      </div>
      <div className="field" style={{ marginBottom: 31 }}>
        <label className="required d-flex align-items-center">
          {t("EditBillings.請求先メールアドレス")}
          <span className="ml-1 d-flex align-items-center" style={{ fontSize: 12, color: "#FF4958" }}>
            <img src={getImageUrl("images/icons/asterisk.svg")} alt="" />
            {t("EditBillings.必須")}
          </span>
        </label>
        <small className="text-muted mb-2" style={{ display: "block" }}>
          {t("EditBillings.※請求書送付先のメールアドレスを入力してください。メールアドレスは請求書にも記載されます｡")}
        </small>
        <input
          className={`form-control${errMessage?.billingEmail ? ` is-invalid` : ""}`}
          type="email"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          style={{ backgroundImage: "none", width: 400 }}
        />
        {errMessage?.billingEmail && <div className="invalid-feedback">{errMessage.billingEmail}</div>}
      </div>
      <div className="field">
        <label className="required">{t("EditBillings.請求先住所")}</label>
        <small className="text-muted mb-2" style={{ display: "block" }}>
          {t(
            "EditBillings.※請求書に記載されます。空欄の場合は、請求書の住所欄が空欄になります｡請求書の送付はPDFのみです。郵送には対応しておりません。",
          )}
        </small>
        <div
          className="output-border-radius"
          style={{
            background: "#fafafa",
            border: "solid 1px #e6e6e6",
            width: 664,
            padding: "30px 30px 40px 30px",
          }}
        >
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              locale: locale,
              appearance: {
                theme: "none",
                rules: {
                  ".Input": {
                    border: "1px solid #cacaca",
                    padding: "13px 15px",
                    fontSize: "14px",
                    outline: "none",
                  },
                  ".Input:focus": {
                    border: "1px solid #777",
                  },
                  ".Input:hover": {
                    border: "1px solid #777",
                  },
                  ".Label": {
                    fontSize: "12px",
                    color: "#777",
                  },
                },
                variables: {
                  colorBackground: "#ffffff",
                  colorText: "#30313d",
                  colorDanger: "#ff4958",
                  fontFamily: "Avenir",
                  spacingUnit: "2px",
                  borderRadius: "12px",
                  gridRowSpacing: "16px",
                  gridColumnSpacing: "16px",
                  colorTextPlaceholder: "#cacaca",
                },
              },
            }}
          >
            <AddressForm billingInfo={billingInfo} ref={addressFormRef} countryCode={countryCode} />
          </Elements>
        </div>
      </div>

      <div className="actions" style={{ marginTop: 32 }}>
        <InfoBtn
          text={t("EditBillings.保存")}
          loading={isSubmit}
          className="test-submit-card"
          submit={() => changeBillings()}
          style={{ fontWeight: 600 }}
        />
      </div>
    </div>
  ) : (
    <PageLoader loadingHeight={"50vh"} />
  );
};

export default BillingInfoForm;
